// PostHog wrapper — single source of truth for analytics + retention events.
//
// Initialised once at app boot when REACT_APP_POSTHOG_KEY is present.
// On native iOS (Capacitor) we still use the JS SDK — PostHog handles that
// case fine via XHR; no native plugin needed for capture.
//
// We surface a tiny `track(event, props)` helper so callers don't need to
// import posthog-js directly.

import posthog from 'posthog-js';
import axios from 'axios';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

let _ready = false;

export const initPostHog = () => {
  if (_ready || !KEY) return;
  try {
    posthog.init(KEY, {
      api_host: HOST,
      person_profiles: 'identified_only', // don't create anon profiles
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false,                  // we use explicit events
      disable_session_recording: true,     // privacy: never record screens
      respect_dnt: false,                  // first-party analytics for our own product
      loaded: () => {
        _ready = true;
        if (process.env.NODE_ENV !== 'production') {
          // Light log to help confirm SDK init in preview / staging builds
          console.info('[posthog] loaded');
        }
      },
    });
    _ready = true;
  } catch (e) {
    // PostHog failures should never break the app
    console.warn('PostHog init failed:', e);
  }
};

export const identifyUser = (user) => {
  if (!KEY || !_ready || !user?.user_id) return;
  try {
    // Audit BROKEN-P2 fix: PostHog identify used to send `email` + `name`
    // in the clear as person properties. That's raw PII in a 3rd-party
    // system — a subpoena or breach would expose our whole user list.
    // We now hash the email (SHA-256 client-side) so PostHog can still
    // dedupe person profiles across devices without storing readable PII.
    // Name is dropped entirely.
    posthog.identify(user.user_id, {
      email_hash: user.email ? _hashEmail(user.email) : undefined,
      role: user.role || 'patient',
      segment: user.segment || undefined,
      language: user.language || undefined,
      auth_provider: user.auth_provider || undefined,
      premium: !!user.premium_status?.is_premium,
    });
  } catch { /* noop */ }
};

// SHA-256 hex digest for stable-but-non-reversible person keys.
// Uses SubtleCrypto when available (browsers, HTTPS). Falls back to a
// prefix truncation so we never accidentally send the raw email.
const _hashEmail = (email) => {
  const lower = (email || '').trim().toLowerCase();
  if (!lower) return undefined;
  try {
    // WebCrypto is async; PostHog wants sync properties, so we cache the
    // sync fallback here and let the async hash overwrite it next tick.
    if (window.crypto?.subtle) {
      const enc = new TextEncoder().encode(lower);
      window.crypto.subtle.digest('SHA-256', enc).then(buf => {
        const hex = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
        try { posthog.people.set({ email_hash: hex }); } catch (_) { /* PostHog may not be ready */ }
      });
    }
  } catch { /* noop */ }
  // Sync fallback — first-8-chars of email prefix, so PostHog still gets
  // *something* that survives across sessions. Never the full email.
  return `pfx:${lower.split('@')[0].slice(0, 8)}`;
};

// Small PII scrubber for the props of every `track()` call. Removes keys
// that commonly carry PHI in the codebase — email, phone, name, message
// bodies. Callers should never send these in the first place, but this
// is a defence-in-depth net.
const _PII_KEYS = new Set([
  'email', 'name', 'full_name', 'phone', 'phone_number', 'mobile',
  'message', 'text', 'body', 'address', 'query',
]);

const _scrubProps = (props) => {
  if (!props || typeof props !== 'object') return props;
  const clean = {};
  for (const [k, v] of Object.entries(props)) {
    if (_PII_KEYS.has(k)) continue;
    // Strip obvious embedded emails / +91 phone numbers from string values.
    if (typeof v === 'string') {
      clean[k] = v
        .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]')
        .replace(/\+?91[- ]?\d{10}/g, '[phone]')
        .slice(0, 400);
    } else {
      clean[k] = v;
    }
  }
  return clean;
};

export const track = (event, props = {}) => {
  if (!KEY || !_ready) return;
  try { posthog.capture(event, _scrubProps(props)); } catch { /* noop */ }
};

/**
 * Server-mirror any event so `/api/admin/analytics/funnels` can compute
 * conversions against our own DB — critical when PostHog is unavailable.
 * Silently no-ops if the caller isn't authenticated.
 * Only ALLOWED_EVENTS (see backend `routes/analytics_events.py`) are accepted.
 */
export const trackServerEvent = async (event, props = {}) => {
  try {
    await axios.post(`${API_BASE}/api/analytics/event`, { event, props }, { withCredentials: true });
  } catch { /* noop — analytics never breaks the app */ }
};

/** Convenience: fire both PostHog + server mirror in a single call. */
export const trackBoth = (event, props = {}) => {
  track(event, props);
  // Fire-and-forget — do not await, do not block callers.
  trackServerEvent(event, props);
};

export const resetAnalytics = () => {
  if (!KEY) return;
  try { posthog.reset(); } catch { /* noop */ }
};

/**
 * Web Vitals → PostHog reporter. Called once at app boot.
 *
 * We track the four industry-standard user-perceived speed metrics from
 * Google's `web-vitals` library (v3+):
 *   • LCP — Largest Contentful Paint (how fast the main content appears)
 *   • INP — Interaction to Next Paint (how snappy the app feels)
 *   • CLS — Cumulative Layout Shift (how jumpy the layout is)
 *   • TTFB — Time to First Byte (how fast the server responded)
 *   • FCP — First Contentful Paint (when the user sees ANYTHING)
 *
 * Each metric fires as a `web_vitals_{name}` PostHog event with the raw
 * value, rating (good / needs-improvement / poor), route path, and a
 * connection-type hint so we can slice performance by network quality.
 *
 * PostHog Insights → filter by `web_vitals_lcp` and group by
 * `properties.rating` for a live LCP-by-route dashboard.
 */
export const initWebVitals = () => {
  if (!KEY) return;
  // Dynamically import so web-vitals never lands in the initial bundle.
  import('web-vitals').then(({ onLCP, onINP, onCLS, onTTFB, onFCP }) => {
    const send = (metric) => {
      try {
        const conn = navigator?.connection || {};
        posthog.capture(`web_vitals_${metric.name.toLowerCase()}`, {
          value: Math.round(metric.value * 100) / 100,
          rating: metric.rating,               // 'good' | 'needs-improvement' | 'poor'
          delta: Math.round(metric.delta * 100) / 100,
          nav_type: metric.navigationType,     // navigate | reload | back-forward | prerender
          path: (window.location?.pathname || '').slice(0, 200),
          effective_type: conn.effectiveType || undefined,  // 4g / 3g / 2g / slow-2g
          rtt_ms: typeof conn.rtt === 'number' ? conn.rtt : undefined,
          downlink_mbps: typeof conn.downlink === 'number' ? conn.downlink : undefined,
          save_data: !!conn.saveData,
        });
      } catch { /* noop */ }
    };
    onLCP(send);
    onINP(send);
    onCLS(send);
    onTTFB(send);
    onFCP(send);
  }).catch(() => { /* web-vitals bundle missing — non-fatal */ });
};

// Convenience event constants — keeps the dashboard taxonomy consistent.
export const EVENTS = {
  // Auth
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  LOGIN_SUCCESS: 'login_success',
  PHONE_OTP_REQUESTED: 'phone_otp_requested',
  PHONE_OTP_VERIFIED: 'phone_otp_verified',
  // Onboarding
  ONBOARDING_STEP: 'onboarding_step',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  // Monetization
  PAYWALL_VIEWED: 'paywall_viewed',
  PAYWALL_DISMISSED: 'paywall_dismissed',
  PREMIUM_PURCHASED: 'premium_purchased',
  PREMIUM_RESTORED: 'premium_restored',
  SUBSCRIPTION_EXPIRED: 'subscription_expired',
  SUBSCRIPTION_RENEWED: 'subscription_renewed',
  // Activation — fires on the user's first meaningful action after signup
  ACTIVATION_COMPLETED: 'activation_completed',
  // Engagement
  AI_SAHELI_CHAT: 'ai_saheli_chat',
  AI_CRY_ANALYZED: 'ai_cry_analyzed',
  AI_MEAL_ANALYZED: 'ai_meal_analyzed',
  AI_REPORT_ANALYZED: 'ai_report_analyzed',
  // Care
  DOCTOR_MESSAGE_SENT: 'doctor_message_sent',
  CONSULT_BOOKED: 'consult_booked',
  // Referral
  REFERRAL_CODE_SHARED: 'referral_code_shared',
  REFERRAL_APPLIED: 'referral_applied',
};
